import React, { useState, useEffect } from "react";
import { useRecoilState } from "recoil";
import { artistListState, songListState, guideListState } from "../atom/atom";
import { Song, Artist, OriginalSong, Guide, Collections } from "../types/type";
import { fetchAndSetData, fetchSongList } from "../api/fetch.api";
import {
  createOriginalSong,
  getOriginalSongById,
  linkSongToOriginal,
  unlinkSongFromOriginal,
  linkGuideToOriginal,
  unlinkGuideFromOriginal,
  fetchAllOriginalSongs,
} from "../api/originalSong.api";
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import AutoMatchingSection from "../components/AutoMatchingSection";

// 탭 타입 정의
type MappingTab = "SONG" | "GUIDE";

const OriginalSongPage: React.FC = () => {
  // 현재 활성화된 탭 상태
  const [activeTab, setActiveTab] = useState<MappingTab>("SONG");

  // 기존 Song 관련 상태
  const [totalSongList, setTotalSongList] = useRecoilState(songListState);
  const [totalArtistList] = useRecoilState(artistListState);
  const [songSearchTerm, setSongSearchTerm] = useState<string>("");
  const [selectedSongs, setSelectedSongs] = useState<Song[]>([]);
  const [linkedSongOriginals, setLinkedSongOriginals] = useState<{
    [songId: string]: OriginalSong;
  }>({});
  const [showSongUnlinkedOnly, setShowSongUnlinkedOnly] =
    useState<boolean>(false);
  const [showAutoMatching, setShowAutoMatching] = useState<boolean>(true);

  // Guide 관련 상태
  const [totalGuideList, setTotalGuideList] = useRecoilState(guideListState);
  const [guideSearchTerm, setGuideSearchTerm] = useState<string>("");
  const [selectedGuides, setSelectedGuides] = useState<Guide[]>([]);
  const [linkedGuideOriginals, setLinkedGuideOriginals] = useState<{
    [guideId: string]: OriginalSong;
  }>({});
  const [showGuideUnlinkedOnly, setShowGuideUnlinkedOnly] =
    useState<boolean>(false);

  // 공통 상태
  const [message, setMessage] = useState<string>("");
  const [isCreatingOriginal, setIsCreatingOriginal] = useState(false);
  const [isLinkingToExisting, setIsLinkingToExisting] = useState(false);
  const [originalSongData, setOriginalSongData] = useState<{
    title: string;
    artist: Artist | null;
    artistId: string | null;
    artistName: string;
    coverArtistIdList: string[];
    isCustomArtist: boolean;
  }>({
    title: "",
    artist: null,
    artistId: null,
    artistName: "",
    coverArtistIdList: [],
    isCustomArtist: false,
  });
  const [existingOriginalSongs, setExistingOriginalSongs] = useState<
    OriginalSong[]
  >([]);
  const [selectedOriginalSong, setSelectedOriginalSong] =
    useState<OriginalSong | null>(null);
  const [originalSongSearchQuery, setOriginalSongSearchQuery] = useState("");
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);
  const [songToUnlink, setSongToUnlink] = useState<Song | null>(null);
  const [guideToUnlink, setGuideToUnlink] = useState<Guide | null>(null);
  const [songGroups, setSongGroups] = useState<SongGroup[]>([]);

  // 컴포넌트 내에 추가할 상태
  const [allOriginalSongs, setAllOriginalSongs] = useState<OriginalSong[]>([]);
  const [isLoadingOriginalSongs, setIsLoadingOriginalSongs] =
    useState<boolean>(false);

  useEffect(() => {
    if (activeTab === "GUIDE" && allOriginalSongs.length === 0) {
      loadAllOriginalSongs();
    }
  }, [activeTab]);

  const loadAllOriginalSongs = async () => {
    if (isLoadingOriginalSongs) return;

    try {
      setIsLoadingOriginalSongs(true);
      const originalSongs = await fetchAllOriginalSongs();
      setAllOriginalSongs(originalSongs);
      console.log(`Loaded ${originalSongs.length} original songs`);
    } catch (error) {
      console.error("Error loading original songs:", error);
      setMessage("원곡 데이터 로드 중 오류가 발생했습니다.");
    } finally {
      setIsLoadingOriginalSongs(false);
    }
  };

  // 노래 그룹 생성 (기존 코드)
  useEffect(() => {
    const groups = groupSongsByTitle(totalSongList);
    setSongGroups(groups);
  }, [totalSongList]);

  // Songs 컬렉션 리스너 설정 (기존 코드)
  useEffect(() => {
    const songsRef = collection(db, Collections.SONG);
    const unsubscribe = onSnapshot(
      songsRef,
      (snapshot) => {
        const updatedSongs: Song[] = [];
        snapshot.forEach((doc) => {
          updatedSongs.push({ ...doc.data(), songId: doc.id } as Song);
        });
        setTotalSongList(updatedSongs);

        // 변경된 문서들만 처리
        snapshot.docChanges().forEach((change) => {
          const songData = {
            ...change.doc.data(),
            songId: change.doc.id,
          } as Song;

          if (change.type === "modified" && songData.originalSongId) {
            getOriginalSongById(songData.originalSongId).then(
              (originalSong) => {
                if (originalSong) {
                  setLinkedSongOriginals((prev) => ({
                    ...prev,
                    [songData.songId]: originalSong,
                  }));
                }
              }
            );
          }
        });
      },
      (error) => {
        console.error("Error listening to songs collection:", error);
        setMessage("곡 목록 업데이트 중 오류가 발생했습니다.");
      }
    );

    return () => unsubscribe();
  }, []);

  // Guide 컬렉션 리스너 설정
  useEffect(() => {
    const guidesRef = collection(db, Collections.GUIDE);
    const unsubscribe = onSnapshot(
      guidesRef,
      (snapshot) => {
        const updatedGuides: Guide[] = [];
        snapshot.forEach((doc) => {
          updatedGuides.push({ ...doc.data(), id: doc.id } as Guide);
        });
        setTotalGuideList(updatedGuides);

        // 변경된 문서들만 처리
        snapshot.docChanges().forEach((change) => {
          const guideData = {
            ...change.doc.data(),
            id: change.doc.id,
          } as Guide;

          if (change.type === "modified" && guideData.originalSongId) {
            getOriginalSongById(guideData.originalSongId).then(
              (originalSong) => {
                if (originalSong) {
                  setLinkedGuideOriginals((prev) => ({
                    ...prev,
                    [guideData.id]: originalSong,
                  }));
                }
              }
            );
          }
        });
      },
      (error) => {
        console.error("Error listening to guides collection:", error);
        setMessage("가이드 목록 업데이트 중 오류가 발생했습니다.");
      }
    );

    return () => unsubscribe();
  }, []);

  // 원곡 정보 리스너 설정 (기존 코드)
  useEffect(() => {
    const originalSongsRef = collection(db, Collections.ORIGINAL_SONG);
    const unsubscribe = onSnapshot(
      originalSongsRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const originalSongData = {
            ...change.doc.data(),
            id: change.doc.id,
          } as OriginalSong;

          // 노래 ID 리스트 처리
          setLinkedSongOriginals((prev) => {
            const updated = { ...prev };
            originalSongData.songIdList?.forEach((songId) => {
              updated[songId] = originalSongData;
            });
            return updated;
          });

          // 가이드 ID 리스트 처리
          setLinkedGuideOriginals((prev) => {
            const updated = { ...prev };
            originalSongData.guideIdList?.forEach((guideId) => {
              updated[guideId] = originalSongData;
            });
            return updated;
          });
        });
      },
      (error) => {
        console.error("Error listening to original songs collection:", error);
        setMessage("원곡 정보 업데이트 중 오류가 발생했습니다.");
      }
    );

    return () => unsubscribe();
  }, []);

  // 그룹 클릭 핸들러 (기존 코드)
  const handleGroupClick = (group: SongGroup) => {
    // 먼저 검색어를 설정하여 UI에 해당 곡들이 표시되도록 함
    setSongSearchTerm(group.displayTitle);

    // 검색된 곡들 중 해당 그룹의 제목과 일치하고 원곡이 연결되지 않은 곡들만 선택
    const songsToSelect = totalSongList.filter(
      (song) =>
        song.title.toLowerCase().trim() === group.title.toLowerCase() &&
        !song.originalSongId // 원곡이 연결되지 않은 곡만 필터링
    );

    setSelectedSongs(songsToSelect);

    // 첫 번째 곡 정보를 기반으로 원곡 정보 자동 설정
    if (songsToSelect.length > 0) {
      const firstSong = songsToSelect[0];
      const artist = totalArtistList.find((a) =>
        firstSong.artistIdList.includes(a.artistId)
      );

      setOriginalSongData({
        title: firstSong.title,
        artist: artist || null,
        artistId: artist?.artistId || firstSong.artistIdList[0] || null,
        artistName: artist?.artistName || firstSong.artistName,
        coverArtistIdList: [],
        isCustomArtist: false,
      });

      // 제목 클립보드에 복사
      setIsLinkingToExisting(true);
      copyToClipboard(firstSong.title.slice(0, 3));
    }
  };

  // 원곡 연결 해제 핸들러
  const handleUnlinkOriginalSong = async () => {
    try {
      if (activeTab === "SONG" && songToUnlink) {
        await unlinkSongFromOriginal(songToUnlink.songId);
        setMessage("원곡 연결이 해제되었습니다.");
      } else if (activeTab === "GUIDE" && guideToUnlink) {
        await unlinkGuideFromOriginal(guideToUnlink.id);
        setMessage("원곡 연결이 해제되었습니다.");
      }

      setShowUnlinkConfirm(false);
      setSongToUnlink(null);
      setGuideToUnlink(null);
    } catch (error) {
      setMessage(`오류 발생: ${error}`);
    }
  };

  // 원곡 생성 핸들러 (기존 코드)
  const handleCreateOriginalSong = async () => {
    try {
      if (!originalSongData.title) {
        setMessage("제목을 입력해주세요.");
        return;
      }
      if (!originalSongData.artistName) {
        setMessage("아티스트 정보를 입력해주세요.");
        return;
      }

      setMessage("원곡 생성 중...");
      const originalSongId = await createOriginalSong({
        title: originalSongData.title,
        artist: originalSongData.isCustomArtist
          ? null
          : originalSongData.artist,
        artistId: originalSongData.isCustomArtist
          ? null
          : originalSongData.artistId,
        artistName: originalSongData.artistName,
        coverArtistIdList: originalSongData.coverArtistIdList,
        madeAt: Timestamp.now(),
      });

      if (activeTab === "SONG") {
        for (const song of selectedSongs) {
          await linkSongToOriginal(song.songId, originalSongId);
        }
      } else {
        for (const guide of selectedGuides) {
          await linkGuideToOriginal(guide.id, originalSongId);
        }
      }

      setMessage("원곡 생성 및 연결 완료!");

      if (activeTab === "SONG") {
        setSelectedSongs([]);
      } else {
        setSelectedGuides([]);
      }

      setIsCreatingOriginal(false);
      setSongSearchTerm("");
      setGuideSearchTerm("");
      setOriginalSongData({
        title: "",
        artist: null,
        artistId: null,
        artistName: "",
        coverArtistIdList: [],
        isCustomArtist: false,
      });
    } catch (error) {
      setMessage(`오류 발생: ${error}`);
    }
  };

  // 인풋 핸들러 (기존 코드)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setOriginalSongData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 검색 핸들러
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (activeTab === "SONG") {
      setSongSearchTerm(value);
    } else {
      setGuideSearchTerm(value);
    }
  };

  // 노래 클릭 핸들러 (기존 코드)
  const handleSongClick = (song: Song) => {
    if (selectedSongs.find((s) => s.songId === song.songId)) {
      setSelectedSongs(selectedSongs.filter((s) => s.songId !== song.songId));
    } else {
      setSelectedSongs([...selectedSongs, song]);
      if (selectedSongs.length === 0) {
        const artist = totalArtistList.find((a) =>
          song.artistIdList.includes(a.artistId)
        );
        setOriginalSongData((prev) => ({
          ...prev,
          title: song.title,
          artist: artist || null,
          artistId: artist?.artistId || song.artistIdList[0] || null,
          artistName: artist?.artistName || song.artistName,
          isCustomArtist: false,
        }));
        setSongSearchTerm(song.title);
        copyToClipboard(song.title);
      }
    }
  };

  // 가이드 클릭 핸들러
  const handleGuideClick = (guide: Guide) => {
    if (selectedGuides.find((g) => g.id === guide.id)) {
      setSelectedGuides(selectedGuides.filter((g) => g.id !== guide.id));
    } else {
      setSelectedGuides([...selectedGuides, guide]);
      if (selectedGuides.length === 0) {
        setGuideSearchTerm(guide.guideTitle);
        copyToClipboard(guide.guideTitle);
      }
    }
  };

  // 클립보드 복사 핸들러 (기존 코드)
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        console.log("제목이 클립보드에 복사되었습니다:", text);
      })
      .catch((err) => {
        console.error("클립보드 복사 실패:", err);
      });
  };

  // 원곡 검색 (기존 코드)
  const searchOriginalSongs = async (searchTerm: string) => {
    try {
      if (!searchTerm.trim()) {
        setExistingOriginalSongs([]);
        return;
      }

      const originalSongsRef = collection(db, Collections.ORIGINAL_SONG);
      const q = query(
        originalSongsRef,
        where("title", ">=", searchTerm),
        where("title", "<=", searchTerm + "\uf8ff")
      );

      const querySnapshot = await getDocs(q);
      const songs: OriginalSong[] = [];

      querySnapshot.forEach((doc) => {
        songs.push({ ...doc.data(), id: doc.id } as OriginalSong);
      });

      setExistingOriginalSongs(songs);
    } catch (error) {
      console.error("Error searching original songs:", error);
      setMessage("원곡 검색 중 오류가 발생했습니다.");
    }
  };

  // 아티스트별 노래 그룹화 (기존 코드)
  const songsByArtist = totalArtistList.reduce(
    (acc: { [key: string]: Song[] }, artist: Artist) => {
      acc[artist.artistId] = totalSongList.filter((song) =>
        song.artistIdList.includes(artist.artistId)
      );
      return acc;
    },
    {}
  );

  // 디바운스 (기존 코드)
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // 디바운스 검색 (기존 코드)
  const debouncedSearch = React.useCallback(
    debounce((term: string) => searchOriginalSongs(term), 300),
    []
  );

  // 원곡 검색 핸들러 (기존 코드)
  const handleOriginalSongSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setOriginalSongSearchQuery(searchTerm);
    debouncedSearch(searchTerm);
  };

  // 필터링된 노래 가져오기
  const getFilteredSongs = () => {
    let filteredSongs: Song[] = [];
    Object.values(songsByArtist).forEach((songs) => {
      filteredSongs = [
        ...filteredSongs,
        ...songs.filter(
          (song) =>
            song.title.toLowerCase().includes(songSearchTerm.toLowerCase()) &&
            (!showSongUnlinkedOnly || !song.originalSongId)
        ),
      ];
    });
    return filteredSongs;
  };

  // 필터링된 가이드 가져오기
  const getFilteredGuides = () => {
    return totalGuideList.filter(
      (guide) =>
        guide.guideTitle
          .toLowerCase()
          .includes(guideSearchTerm.toLowerCase()) &&
        (!showGuideUnlinkedOnly || !guide.originalSongId)
    );
  };

  // 전체 선택 핸들러
  const handleSelectAllFiltered = () => {
    if (activeTab === "SONG") {
      const filteredSongs = getFilteredSongs();
      const currentSelectedIds = new Set(selectedSongs.map((s) => s.songId));
      const newSelectedSongs = [
        ...selectedSongs,
        ...filteredSongs.filter((song) => !currentSelectedIds.has(song.songId)),
      ];

      setSelectedSongs(newSelectedSongs);

      if (selectedSongs.length === 0 && newSelectedSongs.length > 0) {
        const firstSong = newSelectedSongs[0];
        const artist = totalArtistList.find((a) =>
          firstSong.artistIdList.includes(a.artistId)
        );
        setOriginalSongData((prev) => ({
          ...prev,
          title: firstSong.title,
          artist: artist || null,
          artistId: artist?.artistId || firstSong.artistIdList[0] || null,
          artistName: artist?.artistName || firstSong.artistName,
          isCustomArtist: false,
        }));
      }
    } else {
      const filteredGuides = getFilteredGuides();
      const currentSelectedIds = new Set(selectedGuides.map((g) => g.id));
      const newSelectedGuides = [
        ...selectedGuides,
        ...filteredGuides.filter((guide) => !currentSelectedIds.has(guide.id)),
      ];

      setSelectedGuides(newSelectedGuides);
    }
  };

  // 필터 토글 핸들러
  const toggleUnlinkedOnly = () => {
    if (activeTab === "SONG") {
      setShowSongUnlinkedOnly(!showSongUnlinkedOnly);
    } else {
      setShowGuideUnlinkedOnly(!showGuideUnlinkedOnly);
    }
  };

  // 노래 렌더링 (기존 코드)
  const renderSong = (song: Song) => (
    <li
      key={song.songId}
      style={{
        ...styles.songItem,
        ...(selectedSongs.find((s) => s.songId === song.songId)
          ? styles.selectedSongItem
          : {}),
      }}
    >
      <div style={styles.songItemContent}>
        <div>
          <span onClick={() => handleSongClick(song)} style={styles.songTitle}>
            {song.title}
          </span>
        </div>
        {song.originalSongId && linkedSongOriginals[song.songId] && (
          <div style={styles.originalSongInfo}>
            <span style={styles.checkmark}>✓</span>
            <span style={styles.originalSongTitle}>
              {linkedSongOriginals[song.songId].title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSongToUnlink(song);
                setGuideToUnlink(null);
                setShowUnlinkConfirm(true);
              }}
              style={styles.unlinkButton}
            >
              연결해제
            </button>
          </div>
        )}
      </div>
    </li>
  );

  const handleMatchingComplete = (
    pairs: { guideId: string; originalSongId: string }[]
  ) => {
    // 매칭 완료 후 가이드 목록 갱신
    // 실제 구현에서는 fetchGuideList와 같은 함수를 호출해야 할 수 있음
    console.log("Completed mappings:", pairs);

    // 적용된 가이드들에 대해 UI 업데이트
    pairs.forEach(({ guideId, originalSongId }) => {
      getOriginalSongById(originalSongId).then((originalSong) => {
        if (originalSong) {
          setLinkedGuideOriginals((prev) => ({
            ...prev,
            [guideId]: originalSong,
          }));
        }
      });
    });
  };

  // 가이드 렌더링
  const renderGuide = (guide: Guide) => (
    <li
      key={guide.id}
      style={{
        ...styles.guideItem,
        ...(selectedGuides.find((g) => g.id === guide.id)
          ? styles.selectedGuideItem
          : {}),
      }}
    >
      <div style={styles.guideItemContent}>
        <div>
          <span
            onClick={() => handleGuideClick(guide)}
            style={styles.guideTitle}
          >
            {guide.guideTitle} {guide.isMale ? "(남성)" : "(여성)"}
          </span>
        </div>
        {guide.originalSongId && linkedGuideOriginals[guide.id] && (
          <div style={styles.originalSongInfo}>
            <span style={styles.checkmark}>✓</span>
            <span style={styles.originalSongTitle}>
              {linkedGuideOriginals[guide.id].title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSongToUnlink(null);
                setGuideToUnlink(guide);
                setShowUnlinkConfirm(true);
              }}
              style={styles.unlinkButton}
            >
              연결해제
            </button>
          </div>
        )}
      </div>
    </li>
  );

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>원곡 관리 페이지</h1>

      {/* 탭 버튼 추가 */}
      <div style={styles.tabButtons}>
        <button
          onClick={() => setActiveTab("SONG")}
          style={{
            ...styles.tabButton,
            ...(activeTab === "SONG" ? styles.activeTabButton : {}),
          }}
        >
          노래 원곡 매핑
        </button>
        <button
          onClick={() => setActiveTab("GUIDE")}
          style={{
            ...styles.tabButton,
            ...(activeTab === "GUIDE" ? styles.activeTabButton : {}),
          }}
        >
          가이드 원곡 매핑
        </button>
      </div>

      {/* 노래 매핑 탭 */}
      {activeTab === "SONG" && (
        <>
          {/* 동일 제목 곡 그룹 섹션 */}
          {songGroups.length > 0 && (
            <div style={styles.songGroupsSection}>
              <SongGroups
                songGroups={songGroups}
                onGroupClick={handleGroupClick}
              />
            </div>
          )}

          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="곡 제목 검색"
              value={songSearchTerm}
              onChange={handleSearch}
              style={styles.searchInput}
            />
            {songSearchTerm && (
              <button
                onClick={handleSelectAllFiltered}
                style={styles.selectAllButton}
              >
                검색된 곡 전체 선택
              </button>
            )}
            <button
              onClick={toggleUnlinkedOnly}
              style={{
                backgroundColor: showSongUnlinkedOnly ? "#4caf50" : "#f5f5f5",
                color: showSongUnlinkedOnly ? "white" : "#333",
              }}
            >
              원곡 미연결곡만 보기
            </button>
          </div>

          <div style={styles.selectedSongsContainer}>
            <h2 style={styles.subtitle}>선택된 곡: {selectedSongs.length}개</h2>
            {selectedSongs.length > 0 && (
              <div style={styles.selectedSongsList}>
                {selectedSongs.map((song) => (
                  <div key={song.songId} style={styles.selectedSongItem}>
                    {song.title} - {song.artistName}
                    <button
                      onClick={() => handleSongClick(song)}
                      style={styles.removeButton}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div style={styles.buttonGroup}>
                  <button
                    onClick={() => {
                      setIsCreatingOriginal(true);
                      copyToClipboard(originalSongData.title);
                    }}
                    style={styles.createButton}
                  >
                    새 원곡 생성하기
                  </button>
                  <button
                    onClick={() => {
                      setIsLinkingToExisting(true);
                      copyToClipboard(originalSongData.title.slice(0, 3));
                    }}
                    style={styles.linkButton}
                  >
                    기존 원곡에 연결
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={styles.artistGrid}>
            {Object.entries(songsByArtist).map(([artistId, songs]) => {
              const artist = totalArtistList.find(
                (a) => a.artistId === artistId
              );
              const filteredSongs = songs.filter(
                (song) =>
                  song.title
                    .toLowerCase()
                    .includes(songSearchTerm.toLowerCase()) &&
                  (!showSongUnlinkedOnly || !song.originalSongId)
              );

              if (!filteredSongs.length) return null;

              return (
                <div key={artistId} style={styles.artistCard}>
                  <h3 style={styles.artistName}>{artist?.artistName}</h3>
                  <ul style={styles.songList}>
                    {filteredSongs.map(renderSong)}
                  </ul>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 가이드 매핑 탭 */}
      {activeTab === "GUIDE" && (
        <>
          <div style={styles.autoMatchingToggle}>
            <button
              onClick={() => setShowAutoMatching(!showAutoMatching)}
              style={{
                ...styles.toggleButton,
                backgroundColor: showAutoMatching ? "#1976d2" : "#f5f5f5",
                color: showAutoMatching ? "white" : "#333",
              }}
            >
              {showAutoMatching ? "자동 매칭 숨기기" : "자동 매칭 보기"}
            </button>
          </div>

          {showAutoMatching && (
            <AutoMatchingSection
              guides={totalGuideList}
              originalSongs={allOriginalSongs}
              onMatchingComplete={handleMatchingComplete}
              setMessage={setMessage}
            />
          )}
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="가이드 제목 검색"
              value={guideSearchTerm}
              onChange={handleSearch}
              style={styles.searchInput}
            />
            {guideSearchTerm && (
              <button
                onClick={handleSelectAllFiltered}
                style={styles.selectAllButton}
              >
                검색된 가이드 전체 선택
              </button>
            )}
            <button
              onClick={toggleUnlinkedOnly}
              style={{
                backgroundColor: showGuideUnlinkedOnly ? "#4caf50" : "#f5f5f5",
                color: showGuideUnlinkedOnly ? "white" : "#333",
              }}
            >
              원곡 미연결 가이드만 보기
            </button>
          </div>

          <div style={styles.selectedGuidesContainer}>
            <h2 style={styles.subtitle}>
              선택된 가이드: {selectedGuides.length}개
            </h2>
            {selectedGuides.length > 0 && (
              <div style={styles.selectedGuidesList}>
                {selectedGuides.map((guide) => (
                  <div key={guide.id} style={styles.selectedGuideItem}>
                    {guide.guideTitle} {guide.isMale ? "(남성)" : "(여성)"}
                    <button
                      onClick={() => handleGuideClick(guide)}
                      style={styles.removeButton}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div style={styles.buttonGroup}>
                  <button
                    onClick={() => {
                      setIsCreatingOriginal(true);
                      if (selectedGuides.length > 0) {
                        // 선택된 첫 번째 가이드의 제목을 원곡 제목 초기값으로 설정
                        setOriginalSongData((prev) => ({
                          ...prev,
                          title: selectedGuides[0].guideTitle,
                        }));
                        copyToClipboard(selectedGuides[0].guideTitle);
                      }
                    }}
                    style={styles.createButton}
                  >
                    새 원곡 생성하기
                  </button>
                  <button
                    onClick={() => {
                      setIsLinkingToExisting(true);
                      if (selectedGuides.length > 0) {
                        copyToClipboard(
                          selectedGuides[0].guideTitle.slice(0, 3)
                        );
                      }
                    }}
                    style={styles.linkButton}
                  >
                    기존 원곡에 연결
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 성별에 따른 가이드 표시 */}
          <div style={styles.genderTabs}>
            <h2 style={styles.subtitle}>남성 가이드</h2>
            <div style={styles.guideGrid}>
              {getFilteredGuides()
                .filter((guide) => guide.isMale)
                .map(renderGuide)}
            </div>

            <h2 style={{ ...styles.subtitle, marginTop: 24 }}>여성 가이드</h2>
            <div style={styles.guideGrid}>
              {getFilteredGuides()
                .filter((guide) => !guide.isMale)
                .map(renderGuide)}
            </div>
          </div>
        </>
      )}

      {/* 모달 */}
      {showUnlinkConfirm && (songToUnlink || guideToUnlink) && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>원곡 연결 해제</h2>
            <p style={styles.confirmText}>
              "{songToUnlink ? songToUnlink.title : guideToUnlink?.guideTitle}
              "의 원곡 연결을 해제하시겠습니까?
            </p>
            <div style={styles.modalButtons}>
              <button
                onClick={() => {
                  setShowUnlinkConfirm(false);
                  setSongToUnlink(null);
                  setGuideToUnlink(null);
                }}
                style={styles.cancelButton}
              >
                취소
              </button>
              <button
                onClick={handleUnlinkOriginalSong}
                style={styles.deleteButton}
              >
                연결 해제
              </button>
            </div>
          </div>
        </div>
      )}

      {isCreatingOriginal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>원곡 정보 입력</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                제목:
                <input
                  type="text"
                  name="title"
                  value={originalSongData.title}
                  onChange={handleInputChange}
                  style={styles.input}
                />
              </label>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  checked={originalSongData.isCustomArtist}
                  onChange={(e) => {
                    setOriginalSongData((prev) => ({
                      ...prev,
                      isCustomArtist: e.target.checked,
                      artist: e.target.checked ? null : prev.artist,
                      artistId: e.target.checked ? null : prev.artistId,
                      artistName: e.target.checked ? "" : prev.artistName,
                    }));
                  }}
                />
                아티스트 직접 입력
              </label>
            </div>

            {originalSongData.isCustomArtist ? (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  아티스트 이름:
                  <input
                    type="text"
                    name="artistName"
                    value={originalSongData.artistName}
                    onChange={handleInputChange}
                    style={styles.input}
                  />
                </label>
              </div>
            ) : (
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  아티스트 선택:
                  <select
                    value={originalSongData.artistId || ""}
                    onChange={(e) => {
                      const selectedArtist = totalArtistList.find(
                        (a) => a.artistId === e.target.value
                      );
                      setOriginalSongData((prev) => ({
                        ...prev,
                        artist: selectedArtist || null,
                        artistId: selectedArtist?.artistId || null,
                        artistName: selectedArtist?.artistName || "",
                      }));
                    }}
                    style={styles.input}
                  >
                    <option value="">선택하세요</option>
                    {totalArtistList.map((artist) => (
                      <option key={artist.artistId} value={artist.artistId}>
                        {artist.artistName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}

            <div style={styles.modalButtons}>
              <button
                onClick={() => setIsCreatingOriginal(false)}
                style={styles.cancelButton}
              >
                취소
              </button>
              <button
                onClick={handleCreateOriginalSong}
                style={styles.submitButton}
              >
                생성
              </button>
            </div>
          </div>
        </div>
      )}

      {isLinkingToExisting && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={styles.modalTitle}>기존 원곡에 연결</h2>
            <div style={styles.formGroup}>
              <input
                type="text"
                placeholder="원곡 검색..."
                value={originalSongSearchQuery}
                onChange={handleOriginalSongSearch}
                style={styles.input}
              />
              <div style={styles.originalSongList}>
                {existingOriginalSongs.length === 0 ? (
                  <div style={styles.noResults}>
                    {originalSongSearchQuery
                      ? "검색 결과가 없습니다."
                      : "원곡을 검색해주세요."}
                  </div>
                ) : (
                  existingOriginalSongs.map((originalSong) => (
                    <div
                      key={originalSong.id}
                      onClick={() => setSelectedOriginalSong(originalSong)}
                      style={{
                        ...styles.originalSongItem,
                        ...(selectedOriginalSong?.id === originalSong.id
                          ? styles.selectedOriginalSong
                          : {}),
                      }}
                    >
                      {originalSong.title} - {originalSong.artistName}
                    </div>
                  ))
                )}
              </div>
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => {
                  setIsLinkingToExisting(false);
                  setSelectedOriginalSong(null);
                  setOriginalSongSearchQuery("");
                  setExistingOriginalSongs([]);
                }}
                style={styles.cancelButton}
              >
                취소
              </button>
              <button
                onClick={async () => {
                  if (selectedOriginalSong) {
                    try {
                      if (activeTab === "SONG") {
                        for (const song of selectedSongs) {
                          await linkSongToOriginal(
                            song.songId,
                            selectedOriginalSong.id
                          );
                          setLinkedSongOriginals((prev) => ({
                            ...prev,
                            [song.songId]: selectedOriginalSong,
                          }));
                        }
                        setSelectedSongs([]);
                      } else {
                        for (const guide of selectedGuides) {
                          await linkGuideToOriginal(
                            guide.id,
                            selectedOriginalSong.id
                          );
                          setLinkedGuideOriginals((prev) => ({
                            ...prev,
                            [guide.id]: selectedOriginalSong,
                          }));
                        }
                        setSelectedGuides([]);
                      }

                      setMessage("연결 완료!");
                      setIsLinkingToExisting(false);
                      setSelectedOriginalSong(null);
                      setOriginalSongSearchQuery("");
                      setExistingOriginalSongs([]);

                      // 목록 갱신
                      if (activeTab === "SONG") {
                        await fetchAndSetData(fetchSongList, setTotalSongList);
                      } else {
                        // 가이드 목록 갱신 로직 필요
                        // await fetchAndSetData(fetchGuideList, setTotalGuideList);
                      }
                    } catch (error) {
                      setMessage(`오류 발생: ${error}`);
                    }
                  }
                }}
                style={{
                  ...styles.submitButton,
                  opacity: selectedOriginalSong ? 1 : 0.5,
                }}
                disabled={!selectedOriginalSong}
              >
                연결
              </button>
            </div>
          </div>
        </div>
      )}
      {message && <div style={styles.message}>{message}</div>}
    </div>
  );
};

interface SongGroup {
  title: string;
  displayTitle: string;
  songs: Song[];
  count: number;
}

const groupSongsByTitle = (songs: Song[]): SongGroup[] => {
  // 원곡이 매핑되지 않은 곡들만 필터링
  const unmappedSongs = songs.filter((song) => !song.originalSongId);

  const groups: { [key: string]: Song[] } = {};

  unmappedSongs.forEach((song) => {
    // 제목을 키로 사용하여 그룹화
    const normalizedTitle = song.title.trim().toLowerCase();
    if (!groups[normalizedTitle]) {
      groups[normalizedTitle] = [];
    }
    groups[normalizedTitle].push(song);
  });

  // 2개 이상의 곡이 있는 그룹만 반환하고 곡 수가 많은 순으로 정렬
  return Object.entries(groups)
    .filter(([_, songs]) => songs.length > 1)
    .map(([title, songs]) => ({
      title,
      displayTitle: songs[0].title, // 원래 대소문자 유지를 위해
      songs,
      count: songs.length,
    }))
    .sort((a, b) => b.count - a.count); // 곡 수가 많은 순으로 정렬
};

interface SongGroupsProps {
  songGroups: SongGroup[];
  onGroupClick: (group: SongGroup) => void;
}

const SongGroups: React.FC<SongGroupsProps> = ({
  songGroups,
  onGroupClick,
}) => (
  <div style={styles.songGroups}>
    <h3 style={styles.groupSectionTitle}>동일 제목 곡 그룹 (빠른 선택)</h3>
    <div style={styles.groupList}>
      {songGroups.map((group) => (
        <div
          key={group.title}
          style={styles.songGroup}
          onClick={() => onGroupClick(group)}
        >
          <div style={styles.groupInfo}>
            <span style={styles.groupTitle}>{group.displayTitle}</span>
            <span style={styles.groupCount}>{group.count}곡</span>
          </div>
          <button style={styles.selectGroupButton}>선택</button>
        </div>
      ))}
    </div>
  </div>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: 32,
    maxWidth: 1200,
    margin: "0 auto",
    fontFamily: "Arial, sans-serif",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 24,
    color: "#333",
  },
  // 탭 버튼 스타일
  tabButtons: {
    display: "flex",
    marginBottom: 24,
    borderBottom: "1px solid #ddd",
  },
  tabButton: {
    padding: "12px 24px",
    fontSize: 16,
    background: "none",
    border: "none",
    borderBottom: "3px solid transparent",
    cursor: "pointer",
    fontWeight: "bold",
    color: "#666",
  },
  activeTabButton: {
    color: "#1976d2",
    borderBottom: "3px solid #1976d2",
  },
  searchContainer: {
    marginBottom: 24,
    display: "flex",
    gap: 8,
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    padding: 12,
    fontSize: 16,
    border: "1px solid #ddd",
    borderRadius: 4,
    outline: "none",
  },
  selectedSongsContainer: {
    marginBottom: 24,
  },
  selectedGuidesContainer: {
    marginBottom: 24,
  },
  subtitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#333",
  },
  selectedSongsList: {
    backgroundColor: "#f5f5f5",
    padding: 16,
    borderRadius: 4,
  },
  selectedGuidesList: {
    backgroundColor: "#f5f5f5",
    padding: 16,
    borderRadius: 4,
  },
  selectedSongItem: {
    backgroundColor: "#e3f2fd",
    marginBottom: 8,
    padding: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  selectedGuideItem: {
    backgroundColor: "#e3f2fd",
    marginBottom: 8,
    padding: 8,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  removeButton: {
    backgroundColor: "transparent",
    border: "none",
    color: "#f44336",
    cursor: "pointer",
    fontSize: 16,
  },
  createButton: {
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: 4,
    cursor: "pointer",
    marginTop: 16,
  },
  artistGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
  },
  guideGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 16,
  },
  artistCard: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: 16,
  },
  artistName: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
  },
  songList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  songItem: {
    padding: "8px 0",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  guideItem: {
    padding: "8px 0",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  checkmark: {
    color: "#4caf50",
    marginLeft: 8,
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    padding: 24,
    borderRadius: 4,
    width: "100%",
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 16,
    color: "#333",
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: "block",
    marginBottom: 8,
    color: "#666",
  },
  input: {
    width: "100%",
    padding: 8,
    fontSize: 16,
    border: "1px solid #ddd",
    borderRadius: 4,
    marginTop: 4,
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  cancelButton: {
    backgroundColor: "#f5f5f5",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
  },
  submitButton: {
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
  },
  message: {
    position: "fixed",
    bottom: 16,
    right: 16,
    backgroundColor: "#333",
    color: "white",
    padding: 16,
    borderRadius: 4,
    zIndex: 1000,
  },
  buttonGroup: {
    display: "flex",
    gap: 8,
    marginTop: 16,
  },
  linkButton: {
    backgroundColor: "#4caf50",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: 4,
    cursor: "pointer",
  },
  originalSongList: {
    maxHeight: 200,
    overflowY: "auto",
    border: "1px solid #ddd",
    borderRadius: 4,
    marginTop: 8,
  },
  originalSongItem: {
    padding: 8,
    cursor: "pointer",
    borderBottom: "1px solid #eee",
  },
  selectedOriginalSong: {
    backgroundColor: "#e3f2fd",
  },
  noResults: {
    padding: 16,
    textAlign: "center",
    color: "#666",
  },
  songItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  guideItemContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  songTitle: {
    cursor: "pointer",
    flex: 1,
  },
  guideTitle: {
    cursor: "pointer",
    flex: 1,
  },
  originalSongInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  originalSongTitle: {
    fontSize: "14px",
    color: "#666",
  },
  unlinkButton: {
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#f44336",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
  },
  confirmText: {
    marginBottom: 16,
    color: "#333",
    textAlign: "center",
  },
  genderTabs: {
    marginTop: 16,
  },
  songGroups: {
    marginBottom: 24,
    padding: 16,
    backgroundColor: "#e3f2fd",
    borderRadius: 8,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  groupSectionTitle: {
    fontSize: 18,
    marginBottom: 16,
    color: "#1976d2",
    fontWeight: "bold",
  },
  groupList: {
    display: "flex",
    overflowX: "auto",
    gap: 16,
    paddingBottom: 8, // 스크롤바 공간
    WebkitOverflowScrolling: "touch", // iOS에서 부드러운 스크롤 지원
  },
  songGroup: {
    padding: 16,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    border: "1px solid #e0e0e0",
    transition: "transform 0.2s, box-shadow 0.2s",
    minWidth: 220, // 최소 너비 지정
    flex: "0 0 auto", // 크기 고정
  },
  groupInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  groupTitle: {
    fontWeight: "bold",
    fontSize: 16,
    color: "#333",
    textAlign: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  groupCount: {
    fontSize: 14,
    color: "#fff",
    backgroundColor: "#1976d2",
    padding: "4px 10px",
    borderRadius: 16,
    fontWeight: "bold",
  },
  selectGroupButton: {
    backgroundColor: "#4caf50",
    color: "white",
    border: "none",
    padding: "8px 0",
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: "bold",
    transition: "background-color 0.2s",
    width: "100%",
    marginTop: "auto", // 항상 카드 하단에 배치
  },
  songGroupsSection: {
    marginBottom: 24,
  },
  selectAllButton: {
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    padding: "8px 16px",
    borderRadius: 4,
    cursor: "pointer",
  },
  autoMatchingToggle: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  },
  toggleButton: {
    padding: "8px 16px",
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: "bold",
    transition: "background-color 0.2s",
  },
};

export default OriginalSongPage;
